import React, { memo, useCallback, useEffect, useState } from 'react';
import { Map } from 'mapbox-gl';

import { DeleteModal, MapControlButton } from 'modules/ui/components';
import { MinusIcon, PlusIcon, UploadIcon } from 'modules/ui/assets';
import {
  CreateExclusionZoneFlow,
  useOnExclusionAreaClick,
  UploadExclusionZoneFlow,
  useDeleteAllExclusionZones,
} from 'modules/exclusionZones';
import {
  CreateInclusionZoneFlow,
  useOnInclusionAreaClick,
  UploadInclusionZoneFlow,
  useDeleteAllInclusionZones,
} from 'modules/inclusionZones';
import { useUiVisibility } from 'modules/metadata';

import { Container, VisibleFlows } from './styledComponents';
import { Colors } from 'modules/ui/theme/colors';

type Props = {
  isOpen: boolean;
  map: Map;
  hideExclusionZones: () => void;
  showExclusionZones: () => void;
  hideInclusionZones: () => void;
  showInclusionZones: () => void;
  isExclusionZoneVisibilityOn: boolean;
  isInclusionZoneVisibilityOn: boolean;
};

enum Flow {
  UploadExclusionZone = 1,
  AddExclusionZone,
  UploadInclusionZone,
  AddInclusionZone,
}

const ZonesMenu: React.FunctionComponent<Props> = ({
  isOpen,
  map,
  hideExclusionZones,
  hideInclusionZones,
  showExclusionZones,
  showInclusionZones,
  isExclusionZoneVisibilityOn,
  isInclusionZoneVisibilityOn,
}) => {
  const [openedFlow, setOpenedFlow] = useState<null | Flow>(null);
  const [editedZoneId, setEditedZoneId] = useState<number | null>(null);
  const [isDeleteAllExclusionModalOpen, setIsDeleteAllExclusionModalOpen] =
    useState(false);
  const [isDeleteAllInclusionModalOpen, setIsDeleteAllInclusionModalOpen] =
    useState(false);

  const closeDeleteAllExclusionModal = useCallback(
    () => setIsDeleteAllExclusionModalOpen(false),
    [],
  );
  const closeDeleteAllInclusionModal = useCallback(
    () => setIsDeleteAllInclusionModalOpen(false),
    [],
  );

  const {
    loading: deleteAllExclusionLoading,
    handleDeleteAll: handleDeleteAllExclusion,
  } = useDeleteAllExclusionZones(closeDeleteAllExclusionModal);
  const {
    loading: deleteAllInclusionLoading,
    handleDeleteAll: handleDeleteAllInclusion,
  } = useDeleteAllInclusionZones(closeDeleteAllInclusionModal);

  const cancelFlow = useCallback(() => {
    setOpenedFlow((flow) => {
      if (flow === Flow.AddExclusionZone) {
        showExclusionZones();
      }
      if (flow === Flow.AddInclusionZone) {
        showInclusionZones();
      }
      setEditedZoneId(null);
      return null;
    });
  }, [setOpenedFlow, showExclusionZones, showInclusionZones]);
  const editExclusionZone = useCallback((id: number) => {
    setEditedZoneId(id);
    setOpenedFlow(Flow.AddExclusionZone);
  }, []);
  const editInclusionZone = useCallback((id: number) => {
    setEditedZoneId(id);
    setOpenedFlow(Flow.AddInclusionZone);
  }, []);
  useOnExclusionAreaClick(
    map,
    editExclusionZone,
    !!editedZoneId,
    isExclusionZoneVisibilityOn,
  );
  useOnInclusionAreaClick(
    map,
    editInclusionZone,
    !!editedZoneId,
    isInclusionZoneVisibilityOn,
  );
  const { hideUi, showUi } = useUiVisibility();

  useEffect(() => {
    if (
      openedFlow === Flow.AddExclusionZone ||
      openedFlow === Flow.AddInclusionZone
    ) {
      hideUi();
      hideExclusionZones();
      hideInclusionZones();
    } else {
      showUi();
    }
    return (): void => {
      showUi();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openedFlow]);

  return (
    <Container isOpen={isOpen}>
      <MapControlButton
        tooltipText="Delete all exclusion zones"
        onClick={(): void => setIsDeleteAllExclusionModalOpen(true)}
      >
        <MinusIcon fill={Colors.Destructive} />
      </MapControlButton>
      <MapControlButton
        tooltipText="Upload exclusion zones"
        onClick={(): void => setOpenedFlow(Flow.UploadExclusionZone)}
      >
        <UploadIcon fill={Colors.Destructive} />
      </MapControlButton>

      <MapControlButton
        tooltipText="Add exclusion zones"
        onClick={(): void => setOpenedFlow(Flow.AddExclusionZone)}
      >
        <PlusIcon fill={Colors.Destructive} />
      </MapControlButton>
      <span style={{ padding: '0 4px', fontWeight: 600 }}>|</span>
      <MapControlButton
        tooltipText="Delete all inclusion zones"
        onClick={(): void => setIsDeleteAllInclusionModalOpen(true)}
      >
        <MinusIcon fill={Colors.PrimaryLight} />
      </MapControlButton>
      <MapControlButton
        tooltipText="Upload inclusion zones"
        onClick={(): void => setOpenedFlow(Flow.UploadInclusionZone)}
      >
        <UploadIcon fill={Colors.PrimaryLight} />
      </MapControlButton>

      <MapControlButton
        tooltipText="Add inclusion zones"
        onClick={(): void => setOpenedFlow(Flow.AddInclusionZone)}
      >
        <PlusIcon fill={Colors.PrimaryLight} />
      </MapControlButton>
      <VisibleFlows>
        {openedFlow === Flow.AddExclusionZone && (
          <CreateExclusionZoneFlow
            editedExclusionZoneId={editedZoneId}
            map={map}
            onCancel={cancelFlow}
          />
        )}
        {openedFlow === Flow.UploadExclusionZone && (
          <UploadExclusionZoneFlow onCancel={cancelFlow} />
        )}
        {openedFlow === Flow.AddInclusionZone && (
          <CreateInclusionZoneFlow
            editedInclusionZoneId={editedZoneId}
            map={map}
            onCancel={cancelFlow}
          />
        )}
        {openedFlow === Flow.UploadInclusionZone && (
          <UploadInclusionZoneFlow onCancel={cancelFlow} />
        )}
      </VisibleFlows>
      <DeleteModal
        isOpen={isDeleteAllExclusionModalOpen}
        closeModal={closeDeleteAllExclusionModal}
        handleDelete={handleDeleteAllExclusion}
        loading={deleteAllExclusionLoading}
        text="Are you sure you want to delete all your exclusion zones?"
        title="Delete all exclusion zones"
      />
      <DeleteModal
        isOpen={isDeleteAllInclusionModalOpen}
        closeModal={closeDeleteAllInclusionModal}
        handleDelete={handleDeleteAllInclusion}
        loading={deleteAllInclusionLoading}
        text="Are you sure you want to delete all your inclusion zones?"
        title="Delete all inclusion zones"
      />
    </Container>
  );
};

export default memo(ZonesMenu);
